import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import {
  BeneficiaryCountry,
  DonationTracking,
  DonationTrackingService,
  DonationTrackingStatus,
} from '../../services/DonationTrackingService';

const STATUS_COLORS: Record<
  DonationTrackingStatus,
  'default' | 'warning' | 'info' | 'success'
> = {
  RECEIVED: 'default',
  PROCESSING: 'warning',
  DISPATCHING: 'info',
  DELIVERED: 'success',
};

const PAGE_SIZE = 20;

const DonationTrackingListPage: React.FC = () => {
  const [items, setItems] = useState<DonationTracking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<DonationTrackingStatus | ''>('');
  const [countryFilter, setCountryFilter] = useState<BeneficiaryCountry | ''>('');
  const [search, setSearch] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await DonationTrackingService.list({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter || undefined,
        country: countryFilter || undefined,
        search: search.trim() || undefined,
      });
      setItems(result.items);
      setTotalPages(Math.max(1, result.totalPages));
    } catch (e) {
      setError('Failed to load donation tracking records.');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, countryFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleBackfill = async () => {
    setBackfilling(true);
    setError(null);
    setNotice(null);
    try {
      const result = await DonationTrackingService.backfill();
      setNotice(
        `Backfill complete — created ${result.created}, skipped ${result.skipped}${result.failed ? `, failed ${result.failed}` : ''}.`,
      );
      await load();
    } catch {
      setError('Backfill failed.');
    } finally {
      setBackfilling(false);
    }
  };

  const summaryByStatus = useMemo(() => {
    const counts: Record<DonationTrackingStatus, number> = {
      RECEIVED: 0,
      PROCESSING: 0,
      DISPATCHING: 0,
      DELIVERED: 0,
    };
    for (const item of items) {
      counts[item.status] += 1;
    }
    return counts;
  }, [items]);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Donation Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Simulated tracking records for successful donations. Status progression
            is automated by the backend scheduler and can be overridden here.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={handleBackfill}
          disabled={backfilling}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {backfilling ? 'Backfilling…' : 'Backfill missing tracking'}
        </Button>
      </Stack>
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 3 }}
        alignItems={{ md: 'center' }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e: SelectChangeEvent) => {
              setStatusFilter(e.target.value as DonationTrackingStatus | '');
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="RECEIVED">Received</MenuItem>
            <MenuItem value="PROCESSING">Processing</MenuItem>
            <MenuItem value="DISPATCHING">Dispatching</MenuItem>
            <MenuItem value="DELIVERED">Delivered</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Country</InputLabel>
          <Select
            label="Country"
            value={countryFilter}
            onChange={(e: SelectChangeEvent) => {
              setCountryFilter(e.target.value as BeneficiaryCountry | '');
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="EGYPT">Egypt</MenuItem>
            <MenuItem value="GAZA">Gaza</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Search beneficiary"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1);
              load();
            }
          }}
          sx={{ minWidth: 220 }}
        />
        <Stack direction="row" spacing={1}>
          {(
            ['RECEIVED', 'PROCESSING', 'DISPATCHING', 'DELIVERED'] as DonationTrackingStatus[]
          ).map((status) => (
            <Chip
              key={status}
              label={`${status}: ${summaryByStatus[status]}`}
              color={STATUS_COLORS[status]}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Donation</TableCell>
                <TableCell>Cause</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Beneficiary</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>ETA</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  hover
                  key={item.id}
                  component={RouterLink}
                  to={`/donation-tracking/${item.id}`}
                  sx={{ textDecoration: 'none', cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {item.donationId.slice(0, 8)}…
                    </Typography>
                    {item.donation && (
                      <Typography variant="caption" color="text.secondary">
                        {item.donation.amount} {item.donation.currency}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.cause?.title ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.status}
                      color={STATUS_COLORS[item.status]}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.beneficiaryLabel}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.beneficiaryCount} meals · {item.beneficiaryCountry}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.beneficiaryRegion}</TableCell>
                  <TableCell>
                    {item.estimatedDeliveryDate
                      ? new Date(item.estimatedDeliveryDate).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {new Date(item.updatedAt).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tracking records match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
        <Pagination
          page={page}
          count={totalPages}
          onChange={(_, p) => setPage(p)}
          color="primary"
        />
      </Stack>
    </Box>
  );
};

export default DonationTrackingListPage;
